/**
 * Firebase Configuration
 * LegalSyncUp - Swiss Legal Compliance Platform
 *
 * FIREBASE AUTH - ENTERPRISE IDENTITY PROVIDER
 * =============================================
 * Firebase Auth with Google Cloud Identity Platform provides enterprise-grade
 * identity management for multi-tenant scenarios and SSO integrations.
 *
 * CAPABILITIES:
 * - Email/password authentication
 * - OAuth providers (Google, GitHub, Microsoft)
 * - Enterprise SSO via SAML/OIDC (Identity Platform)
 * - Multi-tenant identity management
 * - Session management with automatic token refresh
 *
 * GCP PROJECT: legalsyncup-com
 *
 * ACTIVATION: Set VITE_USE_FIREBASE_AUTH=true
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'legalsyncup-com.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'legalsyncup-com',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'legalsyncup-com.firebasestorage.app',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
};

// Initialize Firebase app (singleton pattern)
let app: FirebaseApp | undefined;
let auth: Auth | undefined;

/**
 * Get or initialize Firebase app
 */
export const getFirebaseApp = (): FirebaseApp => {
  if (!app) {
    const existingApps = getApps();
    if (existingApps.length > 0) {
      app = existingApps[0];
    } else {
      app = initializeApp(firebaseConfig);
    }
  }
  return app;
};

/**
 * Get or initialize Firebase Auth
 */
export const getFirebaseAuth = (): Auth => {
  if (!auth) {
    auth = getAuth(getFirebaseApp());
  }
  return auth;
};

/**
 * Check if Firebase is configured
 */
export const isFirebaseConfigured = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.appId
  );
};

export { firebaseConfig };
